import { useCallback, useEffect, useState } from 'react';

// The api is asked this long after the last keystroke, not at every one.
export const SEARCH_DELAY_MS = 300;
// What the api accepts for `q`; a longer text is refused with 400 SEARCH_INVALID.
export const SEARCH_MAX_LENGTH = 100;
export const DEFAULT_SCOPE = 'all';
// No status filter: every status.
export const DEFAULT_STATUS = '';

/**
 * The text of the search field as the api accepts it: at most
 * `SEARCH_MAX_LENGTH` characters. The cut never leaves half an emoji (or any
 * character written with two code units) at the end: half of it cannot be put
 * in a url, `encodeURIComponent` throws and no read would leave.
 */
export const limitSearchText = (text) => {
  const limited = text.slice(0, SEARCH_MAX_LENGTH);
  const last = limited.charCodeAt(limited.length - 1);
  const isHighSurrogate = last >= 0xd800 && last <= 0xdbff;
  return isHighSurrogate ? limited.slice(0, -1) : limited;
};

/**
 * The filters of the invoice list: the search (JBWEB-1270), the text of the
 * field and where the api looks for it (`all`, `company`, `group` or `number`),
 * and the status (JBWEB-1271), one of those of the badge or `late`. The
 * reminders tab lets the scope be chosen; the client invoices tab no longer
 * shows it, and stays on `all`.
 *
 * `filters` is what the list is read with: the text trimmed, as the api trims
 * it itself (a trailing space asks for nothing new), and only once it has stayed
 * as it is for `SEARCH_DELAY_MS`; the scope and the status as chosen.
 * `controls` is what `InvoiceFilters` shows and changes, at once: what is typed
 * and chosen.
 *
 * Filters outlive a change of month, not the visit of the tab: they are cleared
 * when `enabled` goes false, as the payslip tab clears its own.
 */
const useInvoiceFilters = (enabled) => {
  const [searchText, setSearchText] = useState('');
  const [query, setQuery] = useState('');
  const [scope, setScope] = useState(DEFAULT_SCOPE);
  const [status, setStatus] = useState(DEFAULT_STATUS);

  useEffect(() => {
    const next = searchText.trim();
    if (next === query) return undefined;
    const timer = setTimeout(() => setQuery(next), SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [searchText, query]);

  useEffect(() => {
    if (enabled) return;
    setSearchText('');
    setQuery('');
    setScope(DEFAULT_SCOPE);
    setStatus(DEFAULT_STATUS);
  }, [enabled]);

  // The field has no `maxLength` to give: a longer text, typed or pasted, is cut.
  const changeSearchText = useCallback(
    (text) => setSearchText(limitSearchText(text)),
    [],
  );

  // No wait: the text is emptied for the list too, so the pending delay of a
  // text typed a moment before cannot bring it back.
  const clear = useCallback(() => {
    setSearchText('');
    setQuery('');
    setScope(DEFAULT_SCOPE);
    setStatus(DEFAULT_STATUS);
  }, []);

  return {
    controls: {
      searchText,
      scope,
      onSearchTextChange: changeSearchText,
      onScopeChange: setScope,
      status,
      onStatusChange: setStatus,
    },
    filters: { q: query, scope, status },
    clear,
  };
};

export default useInvoiceFilters;
